export interface MultiplierConfig {
  aggressiveness: number;
  competitive: {
    influence: number;
    positionOffset: number;
    minCompetitors: number;
    maxStaleDays: number | null;
    fadeWithUrgency: boolean;
    closeInAmplifier: boolean;
  };
  resistance: {
    factor: number | null;
  };
  events: {
    responsiveness: number;
  };
  differentials: {
    responsiveness: number;
  };
}

export interface RuleTimeScope {
  startDate?: string;
  endDate?: string;
  date?: string;
  days?: string[];
}

export interface RuleCondition {
  metric: string;
  operator: "<" | "<=" | ">" | ">=" | "==";
  value: number;
}

export const CONDITION_METRICS = [
  { key: "forecastedOccupancy", label: "Forecasted Occupancy", min: 0, max: 100, suffix: "%" },
] as const;

export interface PricingRule {
  id: string;
  label?: string;
  enabled: boolean;
  priority: number;
  timeScope?: RuleTimeScope;
  conditions?: RuleCondition[];
  actions: Record<string, number>;
  expiresAt?: string | null;
  createdAt: string;
  createdBy: string;
}

export interface ChangeHistoryEntry {
  id: string;
  timestamp: string;
  trigger: string;
  changes: Record<string, { old: unknown; new: unknown }>;
  reasoning: string;
}

export interface PricingConfigResponse {
  config: MultiplierConfig;
  rules: PricingRule[];
  changeHistory: ChangeHistoryEntry[];
}

export interface UpdateConfigPayload {
  changes?: Partial<MultiplierConfig>;
  scope: { type: "base" | "rule"; ruleId?: string };
  rule?: Partial<PricingRule>;
  reasoning?: string;
  trigger: "admin";
}

export const ACTION_KEYS = [
  { key: "aggressiveness", label: "Aggressiveness", min: 1, max: 5, showRange: true },
  { key: "competitive.influence", label: "Competitive Influence", min: 0.3, max: 1.0, showRange: true },
  { key: "competitive.positionOffset", label: "Position Offset", min: -10, max: 15, showRange: false },
  { key: "resistance.factor", label: "Resistance Factor", min: 0.3, max: 1.0, showRange: true },
  { key: "events.responsiveness", label: "Events Responsiveness", min: 0.5, max: 2.5, showRange: true },
  { key: "differentials.responsiveness", label: "Differentials Responsiveness", min: 0.5, max: 2.0, showRange: true },
  { key: "boost", label: "Boost", min: -15, max: 25, showRange: false },
] as const;

export type ActionKey = (typeof ACTION_KEYS)[number]["key"];

export const CONDITION_OPERATORS = ["<", "<=", ">", ">=", "=="] as const;

export const DAYS_OF_WEEK = [
  { value: "mon", label: "Mon" },
  { value: "tue", label: "Tue" },
  { value: "wed", label: "Wed" },
  { value: "thu", label: "Thu" },
  { value: "fri", label: "Fri" },
  { value: "sat", label: "Sat" },
  { value: "sun", label: "Sun" },
] as const;
